import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import CommonTable from "../../../common/table/ComonTable";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../common/formfields/CustomSelect";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";

const AmenitiesBooking = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("All Status");
    
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 0
    });
    
    const employeeCode = storageService.getItem("employeeCode");

    const statusOptions = [
        { label: "All Status", value: "All Status" },
        { label: "Pending", value: "Pending" },
        { label: "Approved", value: "Approved" },
        { label: "Rejected", value: "Rejected" }
    ];

    const fetchBookings = async () => {
        setLoading(true);
        try {
            let currentSiteId = null;
            if (employeeCode) {
                const userRes = await apiServices({
                    url: '/ResidentialUser/GetResidentialUserById',
                    method: 'POST',
                    data: { userId: employeeCode }
                });
                if (userRes.success && userRes.data?.data?.[0]) {
                    currentSiteId = userRes.data.data[0].siteId;
                }
            }

            if (!currentSiteId) {
                setBookings([]);
                setLoading(false);
                return;
            }

            const res = await apiServices({
                url: `/ResAmenityBooking/GetResAmenitiesBookingBySiteId/${currentSiteId}`,
                method: 'GET'
            });

            if (res.success && res.data?.IsSuccess && Array.isArray(res.data.data)) {
                const mapped = res.data.data.map(item => ({
                    bookingId: item.BookingId,
                    amenityName: item.AmenityName,
                    guestCount: item.GuestCount,
                    bookingDate: item.BookingDate,
                    startTime: item.StartTime,
                    endTime: item.EndTime,
                    bookedBy: item.UserName,
                    status: item.BookingStatus,
                    isActiveId: item.IsActiveId
                }));
                // Sort descending by ID
                mapped.sort((a, b) => b.bookingId - a.bookingId);
                setBookings(mapped);
            } else {
                setBookings([]);
            }
        } catch (error) {
            console.error("Error fetching bookings:", error);
            toast.error("Failed to fetch bookings");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings();
    }, []);

    const handleStatusUpdate = async (bookingId, newStatus) => {
        try {
            setLoading(true);
            const body = {
                bookingId: bookingId,
                bookingStatus: newStatus
            };

            const res = await apiServices({
                url: `/ResAmenityBooking/approveORrejectAmenityBooking/${bookingId}`,
                method: 'PUT',
                data: body
            });

            if (res.success && res.data?.IsSuccess) {
                toast.success(res.data.Message || `Booking ${newStatus.toLowerCase()} successfully!`);
                setBookings(prev => prev.map(b => 
                    b.bookingId === bookingId ? { ...b, status: newStatus } : b
                ));
            } else {
                toast.error(res.data?.Message || `Unable to ${newStatus.toLowerCase()} booking.`);
            }
        } catch (error) {
            console.error(`Error updating booking to ${newStatus}:`, error);
            toast.error(`Failed to update booking to ${newStatus}.`);
        } finally {
            setLoading(false);
        }
    };

    const filteredBookings = bookings.filter(booking => {
        const matchesSearch = 
            booking.amenityName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            booking.bookedBy?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = statusFilter === 'All Status' || booking.status === statusFilter;
        
        return matchesSearch && matchesStatus;
    });

    const tableHeaders = [
        { label: "SR NO", key: "sno", align: "center" },
        { label: "AMENITY", key: "amenityName", align: "left" },
        { label: "BOOKED BY", key: "bookedBy", align: "left" },
        { label: "DATE", key: "bookingDate", align: "center" },
        { label: "TIME", key: "time", align: "center" },
        { label: "GUESTS", key: "guestCount", align: "center" },
        { label: "STATUS", key: "status", align: "center" },
        { label: "ACTIONS", key: "act", align: "center" }
    ];

    const getStatusStyles = (status) => {
        const s = (status || "").toUpperCase();
        switch (s) {
            case "PENDING": return "bg-[#FEF9C3] text-[#D97706]";
            case "APPROVED": return "bg-[#DCFCE7] text-[#166534]";
            case "REJECTED": return "bg-[#E31F26] text-[#FFFFFF]";
            default: return "bg-gray-100 text-gray-700";
        }
    };

    const renderTableCell = (key, row, idx) => {
        switch (key) {
            case "sno":
                return <span className="font-semibold text-xs">{((pagination.pageNumber - 1) * pagination.pageSize) + row.originalIndex + 1}</span>;
            case "amenityName":
                return <span className="font-bold text-xs text-[#191C1E]">{row.amenityName}</span>;
            case "bookedBy":
                return <span className="font-medium text-xs text-[#64748B]">{row.bookedBy}</span>;
            case "bookingDate":
                return <span className="font-medium text-xs text-[#64748B]">{row.bookingDate ? new Date(row.bookingDate).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" }) : 'N/A'}</span>;
            case "time":
                return <span className="font-medium text-xs text-[#64748B]">{row.startTime} - {row.endTime}</span>;
            case "guestCount":
                return <span className="font-medium text-xs text-gray-700">{row.guestCount}</span>;
            case "status":
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black tracking-wider uppercase ${getStatusStyles(row.status)}`}>
                        {row.status || "—"}
                    </span>
                );
            case "act":
                if (row.status === 'Pending') {
                    return (
                        <div className="flex gap-3 justify-center items-center">
                            <button
                                onClick={() => handleStatusUpdate(row.bookingId, 'Approved')}
                                className="w-8 h-8 rounded-xl bg-[#E31F26] text-white flex items-center justify-center shadow-md active:scale-95 transition-all hover:bg-red-700"
                                title="Approve"
                            >
                                <FontAwesomeIcon icon={faCheck} />
                            </button>
                            <button
                                onClick={() => handleStatusUpdate(row.bookingId, 'Rejected')}
                                className="w-8 h-8 rounded-xl bg-[#E2E8F0] text-[#334155] flex items-center justify-center shadow-sm active:scale-95 transition-all hover:bg-gray-300"
                                title="Reject"
                            >
                                <FontAwesomeIcon icon={faTimes} className="text-lg" />
                            </button>
                        </div>
                    );
                }
                return <span className="text-gray-400 text-xs">-</span>;
            default:
                return row[key] || "-";
        }
    };

    const headerAction = (
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 w-full sm:w-64">
                <input
                    type="text"
                    placeholder="Search by amenity or user..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setPagination(prev => ({ ...prev, pageNumber: 1 }));
                    }}
                    className="w-full pl-10 pr-4 py-2 bg-[#F8FAFC] border border-gray-200 rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 focus:bg-white transition-colors"
                />
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            </div>
            
            <div className="w-full sm:w-40 relative">
                <CustomSelect
                    options={statusOptions}
                    value={statusOptions.find(opt => opt.value === statusFilter)}
                    onChange={(opt) => {
                        setStatusFilter(opt.value);
                        setPagination(prev => ({ ...prev, pageNumber: 1 }));
                    }}
                    placeholder="Status"
                />
            </div>
        </div>
    );

    const displayData = filteredBookings.slice((pagination.pageNumber - 1) * pagination.pageSize, pagination.pageNumber * pagination.pageSize);

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto gap-6 pt-4 scrollbar-hide">
            <Breadcrumbs />
            
            <PageHeadingSection
                mainHeading="Amenities Bookings"
                subHeading="Approve or reject booking requests made by residential tenants."
            />

            <div className="shrink-0 flex-1 flex flex-col">
                <CommonTable
                    loading={loading}
                    tableHeading="Booking Requests"
                    headers={tableHeaders}
                    data={displayData.map((d, i) => ({ ...d, originalIndex: i }))}
                    renderCell={(key, row, idx) => renderTableCell(key, row, idx)}
                    headerAction={headerAction}
                    pagination={{ ...pagination, totalRecords: filteredBookings.length }}
                    onPageChange={(page) => setPagination(prev => ({ ...prev, pageNumber: page }))}
                    onLimitChange={(limit) => setPagination(prev => ({ ...prev, pageSize: limit, pageNumber: 1 }))}
                />
            </div>
        </div>
    );
};

export default AmenitiesBooking;
